// src/index.ts
import { iconSize } from "@brexhq/metal-icon-shared";

// src/Icon.tsx
import { GenericStencil } from "@brexhq/metal-generic-stencil";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Suspense, memo, useMemo } from "react";

// src/iconsByCategory/index.ts
import { lazy } from "react";
var lazilyLoadedIcons = {
  // Category: Accounting
  accrual: lazy(() => import("./Accrual-27AGTMTQ.mjs")),
  accrualWithUpdates: lazy(() => import("./AccrualWithUpdates-UJZKX5RT.mjs")),
  // Category: Ai
  ai: lazy(() => import("./AI-5DC6P4R3.mjs")),
  aiFilled: lazy(() => import("./AIFilled-QCTRZCW2.mjs")),
  // Category: Banking
  autopay: lazy(() => import("./Autopay-WE6CKNHI.mjs")),
  bank: lazy(() => import("./Bank-ACXWZIXT.mjs")),
  bankNote: lazy(() => import("./BankNote-OBLOC2XN.mjs")),
  briefcase: lazy(() => import("./Briefcase-WCC2R3E2.mjs")),
  cashAccountNumber: lazy(() => import("./CashAccountNumber-7AVPYNMB.mjs")),
  cashAddFunds: lazy(() => import("./CashAddFunds-PC24CQCH.mjs")),
  cashInflow: lazy(() => import("./CashInflow-SQUQWEVR.mjs")),
  cashLightning: lazy(() => import("./CashLightning-D7XWXYMH.mjs")),
  cashLocked: lazy(() => import("./CashLocked-RM6QDGK2.mjs")),
  cashOff: lazy(() => import("./CashOff-ZL64JBYZ.mjs")),
  cashOutflow: lazy(() => import("./CashOutflow-5WQG7XWS.mjs")),
  cashtoCash: lazy(() => import("./CashtoCash-ZN5QFZLJ.mjs")),
  cashtoCashLocked: lazy(() => import("./CashtoCashLocked-XUEKNTMH.mjs")),
  cheque: lazy(() => import("./Cheque-O65OGQH7.mjs")),
  chequeDeposit: lazy(() => import("./ChequeDeposit-B4WOMOEQ.mjs")),
  chequeLocked: lazy(() => import("./ChequeLocked-SHXQFIEX.mjs")),
  dollar: lazy(() => import("./Dollar-Q2U5CQXJ.mjs")),
  dollarZero: lazy(() => import("./DollarZero-MARZV3KB.mjs")),
  flagUnitedStates: lazy(() => import("./FlagUnitedStates-GUYRJ4FS.mjs")),
  global: lazy(() => import("./Global-XRWECFZE.mjs")),
  globalAdvanced: lazy(() => import("./GlobalAdvanced-LFE6Q7WY.mjs")),
  globalReimbursement: lazy(() => import("./GlobalReimbursement-UTGAFTCO.mjs")),
  inbox: lazy(() => import("./Inbox-M5HSPB2E.mjs")),
  inboxFilled: lazy(() => import("./InboxFilled-ZOK6C62O.mjs")),
  invoice: lazy(() => import("./Invoice-A5F5G44P.mjs")),
  invoiceAttached: lazy(() => import("./InvoiceAttached-VGCDIQ5H.mjs")),
  invoiceOutgoing: lazy(() => import("./InvoiceOutgoing-BE44MVQU.mjs")),
  invoiceScanned: lazy(() => import("./InvoiceScanned-FDDUWCTP.mjs")),
  percent: lazy(() => import("./Percent-EV22R5ME.mjs")),
  reimbursements: lazy(() => import("./Reimbursements-ATPEPBPF.mjs")),
  statement: lazy(() => import("./Statement-GAPGJU76.mjs")),
  transfer: lazy(() => import("./Transfer-OWPBJ3FI.mjs")),
  vault: lazy(() => import("./Vault-LPOSKFV6.mjs")),
  vendor: lazy(() => import("./Vendor-LSZDHJV7.mjs")),
  vendorOutgoing: lazy(() => import("./VendorOutgoing-DUT5CVB6.mjs")),
  // Category: Card
  card: lazy(() => import("./Card-TCF73NM3.mjs")),
  cardActivate: lazy(() => import("./CardActivate-OYQLMEEH.mjs")),
  cardCreate: lazy(() => import("./CardCreate-B5PSTUKR.mjs")),
  cardCreditRule: lazy(() => import("./CardCreditRule-CZN7VNGY.mjs")),
  cardFilled: lazy(() => import("./CardFilled-VFN3OT2N.mjs")),
  cardTerminate: lazy(() => import("./CardTerminate-ZNMKZ4XL.mjs")),
  cardVendor: lazy(() => import("./CardVendor-WDBKV2DI.mjs")),
  cardVirtual: lazy(() => import("./CardVirtual-BUUHSVWQ.mjs")),
  cards: lazy(() => import("./Cards-JLXOOUGB.mjs")),
  cardsFilled: lazy(() => import("./CardsFilled-RRFKYJDV.mjs")),
  // Category: CardsPhysical
  card1B21: lazy(() => import("./Card1B21-ESKWNTSD.mjs")),
  cardBlack2022: lazy(() => import("./CardBlack2022-MGSVQ6WP.mjs")),
  cardBudget: lazy(() => import("./CardBudget-5PYVKRUE.mjs")),
  cardCopper2022: lazy(() => import("./CardCopper2022-FXTBKMTA.mjs")),
  cardMetalClassic: lazy(() => import("./CardMetalClassic-QBK7MX7O.mjs")),
  cardPlatinum2022: lazy(() => import("./CardPlatinum2022-OOVHQIY2.mjs")),
  // Category: Data
  barChart: lazy(() => import("./BarChart-GTQQLM7O.mjs")),
  chartUp: lazy(() => import("./ChartUp-XJJGY3HV.mjs")),
  circle: lazy(() => import("./Circle-OIT76RMJ.mjs")),
  donutChart: lazy(() => import("./DonutChart-ASSTJSKY.mjs")),
  list: lazy(() => import("./List-QJRQXL65.mjs")),
  pieChart: lazy(() => import("./PieChart-KAJQZUED.mjs")),
  pieChartFilled: lazy(() => import("./PieChartFilled-2LBNK2QX.mjs")),
  scatterPlotChart: lazy(() => import("./ScatterPlotChart-6R7DC2RR.mjs")),
  sort: lazy(() => import("./Sort-ZI7R67SD.mjs")),
  stackedBarChart: lazy(() => import("./StackedBarChart-5FIBN6C3.mjs")),
  status: lazy(() => import("./Status-FKDG3UXI.mjs")),
  trendingDown: lazy(() => import("./TrendingDown-PHKO4BBX.mjs")),
  trendingDownBorder: lazy(() => import("./TrendingDownBorder-2NLSLEPM.mjs")),
  trendingNeutralBorder: lazy(() => import("./TrendingNeutralBorder-5NJU2GFM.mjs")),
  trendingUp: lazy(() => import("./TrendingUp-XK2XM4H6.mjs")),
  trendingUpBorder: lazy(() => import("./TrendingUpBorder-GFDKYSTL.mjs")),
  // Category: DateTime
  calendar: lazy(() => import("./Calendar-DLHOZFLJ.mjs")),
  calendar15: lazy(() => import("./Calendar15-E23L6SUF.mjs")),
  calendar30: lazy(() => import("./Calendar30-BCJBNFPI.mjs")),
  calendar4: lazy(() => import("./Calendar4-ES4OQFCD.mjs")),
  calendar7: lazy(() => import("./Calendar7-TAOHZHSN.mjs")),
  calendarRange: lazy(() => import("./CalendarRange-LQKXWEKG.mjs")),
  clock: lazy(() => import("./Clock-AHRSIEST.mjs")),
  clockMovingBackwards: lazy(() => import("./ClockMovingBackwards-TFLSZVLX.mjs")),
  clockMovingForward: lazy(() => import("./ClockMovingForward-Q5JKJGFN.mjs")),
  daily: lazy(() => import("./Daily-YG4JHYJO.mjs")),
  hourglass: lazy(() => import("./Hourglass-ZUXLW6DQ.mjs")),
  rotate24: lazy(() => import("./Rotate24-CVGU7QYO.mjs")),
  // Category: Feedback
  moodScale1: lazy(() => import("./MoodScale1-IWVUG5TC.mjs")),
  moodScale2: lazy(() => import("./MoodScale2-OMGSUU4P.mjs")),
  moodScale3: lazy(() => import("./MoodScale3-NAKTJCT4.mjs")),
  moodScale4: lazy(() => import("./MoodScale4-ZFFA2577.mjs")),
  moodScale5: lazy(() => import("./MoodScale5-JCMRWYZB.mjs")),
  thumbsDown: lazy(() => import("./ThumbsDown-D6JBVPES.mjs")),
  thumbsUp: lazy(() => import("./ThumbsUp-5JSOFALL.mjs")),
  // Category: Files
  download: lazy(() => import("./Download-65OCXQYH.mjs")),
  fileCsvDefault: lazy(() => import("./FileCSVDefault-4FVHDKFO.mjs")),
  fileCsvError: lazy(() => import("./FileCSVError-JFBKCASN.mjs")),
  fileCsvSuccess: lazy(() => import("./FileCSVSuccess-Y2QQTFB2.mjs")),
  fileDocDefault: lazy(() => import("./FileDOCDefault-IJ3Y4NNP.mjs")),
  fileDocError: lazy(() => import("./FileDOCError-U7MXHDGK.mjs")),
  fileDocSuccess: lazy(() => import("./FileDOCSuccess-WLQCOMRX.mjs")),
  fileGenericDefault: lazy(() => import("./FileGenericDefault-NTJWKP47.mjs")),
  fileGenericError: lazy(() => import("./FileGenericError-6U5LXLNX.mjs")),
  fileGenericSuccess: lazy(() => import("./FileGenericSuccess-SMXVIUEF.mjs")),
  fileHeicDefault: lazy(() => import("./FileHEICDefault-MXBLGAJG.mjs")),
  fileHeicError: lazy(() => import("./FileHEICError-WMVCB2YA.mjs")),
  fileHeicSuccess: lazy(() => import("./FileHEICSuccess-GLVFMXIC.mjs")),
  fileJpegDefault: lazy(() => import("./FileJPEGDefault-QENTF3XN.mjs")),
  fileJpegError: lazy(() => import("./FileJPEGError-FRGU7TTV.mjs")),
  fileJpegSuccess: lazy(() => import("./FileJPEGSuccess-H2LZR7LN.mjs")),
  filePdfDefault: lazy(() => import("./FilePDFDefault-QHXKC3ME.mjs")),
  filePdfError: lazy(() => import("./FilePDFError-DDXFWML6.mjs")),
  filePdfSuccess: lazy(() => import("./FilePDFSuccess-2WYPADYH.mjs")),
  filePngDefault: lazy(() => import("./FilePNGDefault-HIKFWIDH.mjs")),
  filePngError: lazy(() => import("./FilePNGError-AOOKFNYF.mjs")),
  filePngSuccess: lazy(() => import("./FilePNGSuccess-OHHYOSCE.mjs")),
  filePptDefault: lazy(() => import("./FilePPTDefault-HGPWUFO2.mjs")),
  filePptError: lazy(() => import("./FilePPTError-5NXEIP2U.mjs")),
  filePptSuccess: lazy(() => import("./FilePPTSuccess-LCP4B3JP.mjs")),
  incoming: lazy(() => import("./Incoming-QIYOINE2.mjs")),
  outgoing: lazy(() => import("./Outgoing-FZMV2BN7.mjs")),
  upload: lazy(() => import("./Upload-LSWHKVBB.mjs")),
  // Category: Logos
  brexLogoFlag: lazy(() => import("./BrexLogoFlag-5DICTP5N.mjs")),
  netSuite: lazy(() => import("./NetSuite-PZGX6CSB.mjs")),
  slack: lazy(() => import("./Slack-NMSCFEM4.mjs")),
  slackColor: lazy(() => import("./SlackColor-VLM4X2BB.mjs")),
  whatsApp: lazy(() => import("./WhatsApp-INRUA2NY.mjs")),
  whatsAppFilled: lazy(() => import("./WhatsAppFilled-DLC3JMTU.mjs")),
  // Category: MerchantCategories
  advertisingMarketing: lazy(() => import("./AdvertisingMarketing-ZGPPVEYR.mjs")),
  airlineExpenses: lazy(() => import("./AirlineExpenses-YSVWQEGD.mjs")),
  barsNightlife: lazy(() => import("./BarsNightlife-6PONSKAX.mjs")),
  booksNewspapers: lazy(() => import("./BooksNewspapers-LYFB2MKA.mjs")),
  carRental: lazy(() => import("./CarRental-PC2ADC3A.mjs")),
  charity: lazy(() => import("./Charity-UEO4BOAT.mjs")),
  clothing: lazy(() => import("./Clothing-UX32ZYZX.mjs")),
  clubsMemberships: lazy(() => import("./ClubsMemberships-ME4SMTJW.mjs")),
  coffeeShops: lazy(() => import("./CoffeeShops-5RBME3FR.mjs")),
  conferences: lazy(() => import("./Conferences-SD4F6CNJ.mjs")),
  consultantorContractor: lazy(() => import("./ConsultantorContractor-FLOX6BH2.mjs")),
  digitalGoods: lazy(() => import("./DigitalGoods-3MBLJY5K.mjs")),
  electronics: lazy(() => import("./Electronics-7Y55UC5D.mjs")),
  eventExpenses: lazy(() => import("./EventExpenses-LPKJ3RMU.mjs")),
  flowers: lazy(() => import("./Flowers-STORWNML.mjs")),
  foodDelivery: lazy(() => import("./FoodDelivery-MBT47BXL.mjs")),
  furniture: lazy(() => import("./Furniture-JFVKAFY6.mjs")),
  gambling: lazy(() => import("./Gambling-TBMJHQIU.mjs")),
  gasFuel: lazy(() => import("./GasFuel-LQTEL53X.mjs")),
  generalMerchandise: lazy(() => import("./GeneralMerchandise-GHA35JEA.mjs")),
  grocery: lazy(() => import("./Grocery-EILRQZ7F.mjs")),
  labEquipment: lazy(() => import("./LabEquipment-QQML2LLD.mjs")),
  legalServices: lazy(() => import("./LegalServices-KLZ4VUBF.mjs")),
  lodging: lazy(() => import("./Lodging-SEAOVFPK.mjs")),
  medicalExpenses: lazy(() => import("./MedicalExpenses-44AR2BS2.mjs")),
  officeSupplies: lazy(() => import("./OfficeSupplies-PGPENGQ2.mjs")),
  other: lazy(() => import("./Other-IU4BXYFV.mjs")),
  parking: lazy(() => import("./Parking-DCOO4KIT.mjs")),
  publicTransportation: lazy(() => import("./PublicTransportation-UDMYTK4E.mjs")),
  rent: lazy(() => import("./Rent-YODA5X5M.mjs")),
  restaurants: lazy(() => import("./Restaurants-VUSAGZEG.mjs")),
  rideshareTaxi: lazy(() => import("./RideshareTaxi-33WYKAYV.mjs")),
  servers: lazy(() => import("./Servers-DPA3ZWHR.mjs")),
  shipping: lazy(() => import("./Shipping-MJRCNZYC.mjs")),
  software: lazy(() => import("./Software-4ICYXUOO.mjs")),
  softwareRecurring: lazy(() => import("./SoftwareRecurring-YB22IY6A.mjs")),
  telephony: lazy(() => import("./Telephony-KQPOJUA3.mjs")),
  trainingEducation: lazy(() => import("./TrainingEducation-YOKR6UJX.mjs")),
  travel: lazy(() => import("./Travel-LRJYQ6UB.mjs")),
  travelWiFi: lazy(() => import("./TravelWiFi-44I47FVR.mjs")),
  // Category: Navigation
  arrowDown: lazy(() => import("./ArrowDown-4CTROR36.mjs")),
  arrowDownLeft: lazy(() => import("./ArrowDownLeft-Q6J2B5JH.mjs")),
  arrowDownRight: lazy(() => import("./ArrowDownRight-4PRQTSJG.mjs")),
  arrowLeft: lazy(() => import("./ArrowLeft-L5ZHLRAP.mjs")),
  arrowRight: lazy(() => import("./ArrowRight-AY2VIU37.mjs")),
  arrowUp: lazy(() => import("./ArrowUp-OLIBRTDU.mjs")),
  arrowUpLeft: lazy(() => import("./ArrowUpLeft-76WEOQDL.mjs")),
  arrowUpRight: lazy(() => import("./ArrowUpRight-ZT2ZWXXE.mjs")),
  budget: lazy(() => import("./Budget-VFLNNNIJ.mjs")),
  budgetFilled: lazy(() => import("./BudgetFilled-XRIB4UMI.mjs")),
  budgetNested: lazy(() => import("./BudgetNested-ALQYKFKA.mjs")),
  cash: lazy(() => import("./Cash-ZRUM6YZY.mjs")),
  cashFilled: lazy(() => import("./CashFilled-6FWV3VIR.mjs")),
  chevronDown: lazy(() => import("./ChevronDown-B4ZIRO2Q.mjs")),
  chevronLeft: lazy(() => import("./ChevronLeft-2XT3TMPF.mjs")),
  chevronRight: lazy(() => import("./ChevronRight-ZJMAI2TA.mjs")),
  chevronUp: lazy(() => import("./ChevronUp-CRGJQQTZ.mjs")),
  close: lazy(() => import("./Close-GP5IJ2ET.mjs")),
  codeEditorBottom: lazy(() => import("./CodeEditorBottom-YM5SC5TW.mjs")),
  codeEditorRight: lazy(() => import("./CodeEditorRight-M4XLSQ6L.mjs")),
  home: lazy(() => import("./Home-AHMJ2NXI.mjs")),
  homeFilled: lazy(() => import("./HomeFilled-RLPWRWSX.mjs")),
  integrations: lazy(() => import("./Integrations-U5QYNIUJ.mjs")),
  integrationsFilled: lazy(() => import("./IntegrationsFilled-5DGUTBOS.mjs")),
  menu: lazy(() => import("./Menu-ADNIJN5Q.mjs")),
  overflowHorizontal: lazy(() => import("./OverflowHorizontal-FJBKD4PR.mjs")),
  overflowVertical: lazy(() => import("./OverflowVertical-SMS62OQU.mjs")),
  productLog: lazy(() => import("./ProductLog-MAEIRDHH.mjs")),
  sandbox: lazy(() => import("./Sandbox-X6CPXV3U.mjs")),
  sandboxFilled: lazy(() => import("./SandboxFilled-HLSNJ6DM.mjs")),
  settings: lazy(() => import("./Settings-CK6ZXDLE.mjs")),
  settingsFilled: lazy(() => import("./SettingsFilled-LLTYX6ND.mjs")),
  sidebar: lazy(() => import("./Sidebar-LD4UNA7E.mjs")),
  signOut: lazy(() => import("./SignOut-QX2LWFON.mjs")),
  tasks: lazy(() => import("./Tasks-ZYAOYBMW.mjs")),
  tasksFilled: lazy(() => import("./TasksFilled-GHB2PO2P.mjs")),
  themeDark: lazy(() => import("./ThemeDark-ATDQOBZT.mjs")),
  themeLight: lazy(() => import("./ThemeLight-2SAPRO2H.mjs")),
  wallet: lazy(() => import("./Wallet-WZKAZNKF.mjs")),
  walletFilled: lazy(() => import("./WalletFilled-YSQEWPZU.mjs")),
  // Category: Objects
  camera: lazy(() => import("./Camera-5G7VBYSM.mjs")),
  chatSupport: lazy(() => import("./ChatSupport-BTM5R6U6.mjs")),
  chatSupportFilled: lazy(() => import("./ChatSupportFilled-ZUP6CBCX.mjs")),
  code: lazy(() => import("./Code-XZ53NOLZ.mjs")),
  component: lazy(() => import("./Component-LSICGS75.mjs")),
  computer: lazy(() => import("./Computer-MV4GPRTJ.mjs")),
  contactless: lazy(() => import("./Contactless-FXQURIQH.mjs")),
  contactlessFilled: lazy(() => import("./ContactlessFilled-4RQD3OPX.mjs")),
  cursor: lazy(() => import("./Cursor-PSZKRJFB.mjs")),
  cursorClicking: lazy(() => import("./CursorClicking-A4DWLLP5.mjs")),
  cursorClickingFilled: lazy(() => import("./CursorClickingFilled-O5EKVY54.mjs")),
  cursorFilled: lazy(() => import("./CursorFilled-5KITRH7N.mjs")),
  data: lazy(() => import("./Data-DOPSZY3G.mjs")),
  dataFilled: lazy(() => import("./DataFilled-5F6LCGBA.mjs")),
  discount: lazy(() => import("./Discount-VYYKOMVX.mjs")),
  email: lazy(() => import("./Email-AEB2LX42.mjs")),
  emailFilled: lazy(() => import("./EmailFilled-N6CRW62Z.mjs")),
  emailOpen: lazy(() => import("./EmailOpen-RRDRP7CK.mjs")),
  emailOpenFilled: lazy(() => import("./EmailOpenFilled-7S6BVYEP.mjs")),
  faceId: lazy(() => import("./FaceID-WIRN6HNH.mjs")),
  giftCard: lazy(() => import("./GiftCard-UIQDKPPW.mjs")),
  giftCardFilled: lazy(() => import("./GiftCardFilled-F4AGUCIL.mjs")),
  headphones: lazy(() => import("./Headphones-XEUT5BPF.mjs")),
  image: lazy(() => import("./Image-N4XVNHR5.mjs")),
  imageFilled: lazy(() => import("./ImageFilled-2HT3R5HF.mjs")),
  mapping: lazy(() => import("./Mapping-WFBQIMYV.mjs")),
  microphone: lazy(() => import("./Microphone-ZEEO66V2.mjs")),
  microphoneFilled: lazy(() => import("./MicrophoneFilled-2VLQBZ47.mjs")),
  microphoneOff: lazy(() => import("./MicrophoneOff-2MEHYEXT.mjs")),
  microphoneOffFilled: lazy(() => import("./MicrophoneOffFilled-B7XKGEP2.mjs")),
  notification: lazy(() => import("./Notification-7ANEPOQ2.mjs")),
  notificationDot: lazy(() => import("./NotificationDot-RNSPJKAN.mjs")),
  notificationFilled: lazy(() => import("./NotificationFilled-NURI6DJU.mjs")),
  notificationOff: lazy(() => import("./NotificationOff-BSCTY46Y.mjs")),
  notificationWithBadge: lazy(() => import("./NotificationWithBadge-YYCQCDUW.mjs")),
  notificationWithBadgeFilled: lazy(() => import("./NotificationWithBadgeFilled-XSNUMEYT.mjs")),
  phone: lazy(() => import("./Phone-HEERGV4W.mjs")),
  phoneFilled: lazy(() => import("./PhoneFilled-U5KBE5YU.mjs")),
  pin: lazy(() => import("./Pin-74XWRZHU.mjs")),
  pinFilled: lazy(() => import("./PinFilled-YLJZVWM3.mjs")),
  policy: lazy(() => import("./Policy-GSPNXSES.mjs")),
  policyFilled: lazy(() => import("./PolicyFilled-52C3IL66.mjs")),
  referrals: lazy(() => import("./Referrals-7THI4BD6.mjs")),
  referralsFilled: lazy(() => import("./ReferralsFilled-FKCGKPUW.mjs")),
  rewards: lazy(() => import("./Rewards-C2E5K44B.mjs")),
  rewardsFilled: lazy(() => import("./RewardsFilled-HKF4NREI.mjs")),
  rule: lazy(() => import("./Rule-T5VSY65F.mjs")),
  section: lazy(() => import("./Section-2OM42646.mjs")),
  security: lazy(() => import("./Security-U6I6PC7Y.mjs")),
  securityFilled: lazy(() => import("./SecurityFilled-HO43SY63.mjs")),
  securityKey: lazy(() => import("./SecurityKey-BYGLXINT.mjs")),
  securityKeyFilled: lazy(() => import("./SecurityKeyFilled-UQ25ZFAK.mjs")),
  smartphone: lazy(() => import("./Smartphone-UEFXQ7N3.mjs")),
  star: lazy(() => import("./Star-FC6NSLVM.mjs")),
  starFilled: lazy(() => import("./StarFilled-BTI3JWMS.mjs")),
  starHalfFilled: lazy(() => import("./StarHalfFilled-SEVWCJPE.mjs")),
  touchId: lazy(() => import("./TouchID-EX3HOP4S.mjs")),
  unpin: lazy(() => import("./Unpin-34EYYQRO.mjs")),
  unpinFilled: lazy(() => import("./UnpinFilled-XOEH3NGX.mjs")),
  // Category: Operations
  add: lazy(() => import("./Add-LFT45T6M.mjs")),
  add12pxOnly: lazy(() => import("./Add12pxOnly-KJH6KZ2V.mjs")),
  addinCircle: lazy(() => import("./AddinCircle-B2DXMYQB.mjs")),
  addinCircleFilled: lazy(() => import("./AddinCircleFilled-RAM443HM.mjs")),
  archive: lazy(() => import("./Archive-SJNWJV73.mjs")),
  archiveFilled: lazy(() => import("./ArchiveFilled-ZMLTR3RY.mjs")),
  cardLayout: lazy(() => import("./CardLayout-236LLR4A.mjs")),
  columns: lazy(() => import("./Columns-3KZMBH67.mjs")),
  copy: lazy(() => import("./Copy-Q2QHVSLQ.mjs")),
  copyFilled: lazy(() => import("./CopyFilled-6F434TIN.mjs")),
  dataGridDensityCompact: lazy(() => import("./DataGridDensityCompact-4RR3L4S5.mjs")),
  dataGridDisplayDetailed: lazy(() => import("./DataGridDisplayDetailed-UHO5EBR3.mjs")),
  dataGridDisplaySimple: lazy(() => import("./DataGridDisplaySimple-IWZLGYJ6.mjs")),
  dataGridDividers: lazy(() => import("./DataGridDividers-D4GIJ2IW.mjs")),
  dataGridNoDividers: lazy(() => import("./DataGridNoDividers-E3WLQLSZ.mjs")),
  dragHandle: lazy(() => import("./DragHandle-LGVBODLH.mjs")),
  edit: lazy(() => import("./Edit-HE4O4FBD.mjs")),
  externalLink: lazy(() => import("./ExternalLink-ZDZHBKUJ.mjs")),
  eyeHide: lazy(() => import("./EyeHide-AAU4Q7Q4.mjs")),
  eyeReveal: lazy(() => import("./EyeReveal-HAQHULFO.mjs")),
  filter: lazy(() => import("./Filter-PQFJHB4D.mjs")),
  link: lazy(() => import("./Link-YV5QT6RT.mjs")),
  lock: lazy(() => import("./Lock-RW3NJDFL.mjs")),
  lockFilled: lazy(() => import("./LockFilled-5BMHA4IX.mjs")),
  math: lazy(() => import("./Math-NQ77MVR5.mjs")),
  maximize: lazy(() => import("./Maximize-XJT762KY.mjs")),
  minimize: lazy(() => import("./Minimize-D7YJ6BNM.mjs")),
  pauseFilled: lazy(() => import("./PauseFilled-7SN4OGKM.mjs")),
  playFilled: lazy(() => import("./PlayFilled-FW5IO43Y.mjs")),
  playInCircle: lazy(() => import("./PlayInCircle-LA6XOWNH.mjs")),
  remove: lazy(() => import("./Remove-JP7TZKU7.mjs")),
  repeat: lazy(() => import("./Repeat-I2TVMMKV.mjs")),
  return: lazy(() => import("./Return-VQJECCNM.mjs")),
  rotate: lazy(() => import("./Rotate-EQT7V3SB.mjs")),
  search: lazy(() => import("./Search-5AZAUNFY.mjs")),
  send: lazy(() => import("./Send-NVI6PN6Q.mjs")),
  snooze: lazy(() => import("./Snooze-N7JJ5XNY.mjs")),
  snoozeFilled: lazy(() => import("./SnoozeFilled-D3KYQ4UD.mjs")),
  sync: lazy(() => import("./Sync-MRV5P265.mjs")),
  tableDensity: lazy(() => import("./TableDensity-57KJGE2S.mjs")),
  tableFilter: lazy(() => import("./TableFilter-XWTOQ45W.mjs")),
  tableGroup: lazy(() => import("./TableGroup-YPKKYC54.mjs")),
  then: lazy(() => import("./Then-Q5NGDL7H.mjs")),
  trash: lazy(() => import("./Trash-QLM4JGPZ.mjs")),
  trashFilled: lazy(() => import("./TrashFilled-QTQ42K7L.mjs")),
  typography: lazy(() => import("./Typography-BHMLQRIP.mjs")),
  unlock: lazy(() => import("./Unlock-7G4ZMPXW.mjs")),
  unlockFilled: lazy(() => import("./UnlockFilled-ASSVJ7WU.mjs")),
  // Category: Spend
  autogenerated: lazy(() => import("./Autogenerated-3XL6FDY5.mjs")),
  billDenied: lazy(() => import("./BillDenied-O3RLPACA.mjs")),
  billError: lazy(() => import("./BillError-FW4ACGUL.mjs")),
  billPaid: lazy(() => import("./BillPaid-2YRSDP7K.mjs")),
  billProcessing: lazy(() => import("./BillProcessing-MWVZPFOE.mjs")),
  billScheduled: lazy(() => import("./BillScheduled-2OG7HCIX.mjs")),
  billSubmitted: lazy(() => import("./BillSubmitted-6B2QY6HW.mjs")),
  cardexpenseApproved: lazy(() => import("./CardexpenseApproved-ZFW25SZ7.mjs")),
  cardexpenseDenied: lazy(() => import("./CardexpenseDenied-GYI572G6.mjs")),
  cardexpenseOutofpolicy: lazy(() => import("./CardexpenseOutofpolicy-T42OMIR6.mjs")),
  cardexpenseSubmitted: lazy(() => import("./CardexpenseSubmitted-GOSDQZKS.mjs")),
  deadlineCompleted: lazy(() => import("./DeadlineCompleted-F2G4SAA6.mjs")),
  deadlineDue: lazy(() => import("./DeadlineDue-Q5XYV54S.mjs")),
  deadlineOverdue: lazy(() => import("./DeadlineOverdue-HSJG57BV.mjs")),
  dispute: lazy(() => import("./Dispute-5JSZMOKG.mjs")),
  disputeFilled: lazy(() => import("./DisputeFilled-XJZMFZY6.mjs")),
  expenses: lazy(() => import("./Expenses-SLO4OT7Z.mjs")),
  expensesFilled: lazy(() => import("./ExpensesFilled-CYZWHCDK.mjs")),
  flag: lazy(() => import("./Flag-HJNIS2YG.mjs")),
  flagFilled: lazy(() => import("./FlagFilled-FCKDEWSI.mjs")),
  memo: lazy(() => import("./Memo-IXN6JCZK.mjs")),
  memoAdd: lazy(() => import("./MemoAdd-ZXV5VY5Z.mjs")),
  memoAdded: lazy(() => import("./MemoAdded-PCRUJGUM.mjs")),
  memoAutogenerated: lazy(() => import("./MemoAutogenerated-WISEMHTN.mjs")),
  memoMissing: lazy(() => import("./MemoMissing-7VTWBJ35.mjs")),
  receipt: lazy(() => import("./Receipt-OVOR4I7W.mjs")),
  receiptAdd: lazy(() => import("./ReceiptAdd-2BZOPRQH.mjs")),
  receiptAdded: lazy(() => import("./ReceiptAdded-BYGM7D4H.mjs")),
  receiptAutogenerated: lazy(() => import("./ReceiptAutogenerated-PVBFX467.mjs")),
  receiptFilled: lazy(() => import("./ReceiptFilled-PFSZMV2B.mjs")),
  receiptPending: lazy(() => import("./ReceiptPending-CUSSKVBO.mjs")),
  receiptWarning: lazy(() => import("./ReceiptWarning-OREG5DFH.mjs")),
  receipts: lazy(() => import("./Receipts-D735WVQL.mjs")),
  receiptsAdded: lazy(() => import("./ReceiptsAdded-SFYO4I6F.mjs")),
  reimbursementCanceled: lazy(() => import("./ReimbursementCanceled-JH4RE3HC.mjs")),
  reimbursementDenied: lazy(() => import("./ReimbursementDenied-4N2YXIEP.mjs")),
  reimbursementFailed: lazy(() => import("./ReimbursementFailed-GO7MHRJH.mjs")),
  reimbursementPaid: lazy(() => import("./ReimbursementPaid-J6WAXRHG.mjs")),
  reimbursementScheduled: lazy(() => import("./ReimbursementScheduled-6IDDUFKE.mjs")),
  reimbursementSubmitted: lazy(() => import("./ReimbursementSubmitted-6XX54ULP.mjs")),
  rewardRedeemed: lazy(() => import("./RewardRedeemed-GU2TTOZA.mjs")),
  splitExpense: lazy(() => import("./SplitExpense-7UYZBD64.mjs")),
  // Category: Status
  checkmark: lazy(() => import("./Checkmark-AZKF3QTC.mjs")),
  checkmarkinCircle: lazy(() => import("./CheckmarkinCircle-LI4Y5FMT.mjs")),
  checkmarkinCircleFilled: lazy(() => import("./CheckmarkinCircleFilled-BMWKYKQQ.mjs")),
  closeinCircle: lazy(() => import("./CloseinCircle-TMZSWJGX.mjs")),
  closeinCircleFilled: lazy(() => import("./CloseinCircleFilled-6JY5ERQQ.mjs")),
  error: lazy(() => import("./Error-H54LBQI4.mjs")),
  errorFilled: lazy(() => import("./ErrorFilled-PYUPNIEC.mjs")),
  fourCorners: lazy(() => import("./FourCorners-7GKJ2IJP.mjs")),
  help: lazy(() => import("./Help-3R5VHWET.mjs")),
  helpFilled: lazy(() => import("./HelpFilled-XU6NWW7D.mjs")),
  information: lazy(() => import("./Information-SJSPZBNB.mjs")),
  informationFilled: lazy(() => import("./InformationFilled-4STAQ6SJ.mjs")),
  lightning: lazy(() => import("./Lightning-Z35FL5SD.mjs")),
  lightningFilled: lazy(() => import("./LightningFilled-TDWEBIL7.mjs")),
  lightningOff: lazy(() => import("./LightningOff-PDSM6H65.mjs")),
  lightningOffFilled: lazy(() => import("./LightningOffFilled-Z7IK2V5R.mjs")),
  loop: lazy(() => import("./Loop-V4ZAZOXA.mjs")),
  terminated: lazy(() => import("./Terminated-3P7LCMEM.mjs")),
  warning: lazy(() => import("./Warning-2L5J5JAT.mjs")),
  warningFilled: lazy(() => import("./WarningFilled-4KCTICLH.mjs")),
  // Category: Travel
  flightArriving: lazy(() => import("./FlightArriving-3KNQOY4A.mjs")),
  flightArrivingFilled: lazy(() => import("./FlightArrivingFilled-JVEZQ55L.mjs")),
  flightDeparting: lazy(() => import("./FlightDeparting-QLEBAB24.mjs")),
  flightDepartingFilled: lazy(() => import("./FlightDepartingFilled-3JKHQXQW.mjs")),
  flightInflight: lazy(() => import("./FlightInflight-SZAZWMQ2.mjs")),
  oneWayTrip: lazy(() => import("./OneWayTrip-ESY5YV57.mjs")),
  roundTrip: lazy(() => import("./RoundTrip-O5YK2MZ2.mjs")),
  seat: lazy(() => import("./Seat-XETKILPN.mjs")),
  tripDestination: lazy(() => import("./TripDestination-LRAT6BZR.mjs")),
  tripOrigin: lazy(() => import("./TripOrigin-NR7WV3QE.mjs")),
  tripStop: lazy(() => import("./TripStop-OWKZOMPG.mjs")),
  // Category: Users
  company: lazy(() => import("./Company-EGDJKWBM.mjs")),
  department: lazy(() => import("./Department-7QOONT2J.mjs")),
  departmentFilled: lazy(() => import("./DepartmentFilled-N2LO36RC.mjs")),
  signature: lazy(() => import("./Signature-UL3MYIHT.mjs")),
  user: lazy(() => import("./User-KGECKIRH.mjs")),
  userAdd: lazy(() => import("./UserAdd-FOGVCGQQ.mjs")),
  userAddFilled: lazy(() => import("./UserAddFilled-2HMIME3A.mjs")),
  userApproved: lazy(() => import("./UserApproved-4EWRRMEG.mjs")),
  userApprovedFilled: lazy(() => import("./UserApprovedFilled-4VNT47R6.mjs")),
  userCheckmark: lazy(() => import("./UserCheckmark-6DAMOMKV.mjs")),
  userCheckmarkFilled: lazy(() => import("./UserCheckmarkFilled-PZRNTOOT.mjs")),
  userFilled: lazy(() => import("./UserFilled-RGXU6SGK.mjs")),
  userLock: lazy(() => import("./UserLock-HCMYKP5R.mjs")),
  userLockFilled: lazy(() => import("./UserLockFilled-PD6YHJVG.mjs")),
  userRemove: lazy(() => import("./UserRemove-2PFQ3KWH.mjs")),
  userRemoveFilled: lazy(() => import("./UserRemoveFilled-NWS3VNRH.mjs")),
  userTerminate: lazy(() => import("./UserTerminate-XJQ456BR.mjs")),
  userTerminateFilled: lazy(() => import("./UserTerminateFilled-ZMZFODAT.mjs")),
  userTransfer: lazy(() => import("./UserTransfer-U5XCK3UT.mjs")),
  userTransferFilled: lazy(() => import("./UserTransferFilled-7NHAEAYU.mjs")),
  users: lazy(() => import("./Users-TZE7R2PL.mjs")),
  usersAdd: lazy(() => import("./UsersAdd-Q3CIJQNY.mjs")),
  usersAddFilled: lazy(() => import("./UsersAddFilled-IUN4EEYQ.mjs")),
  usersAutomated: lazy(() => import("./UsersAutomated-Y7NU3H67.mjs")),
  usersAutomatedFilled: lazy(() => import("./UsersAutomatedFilled-SA5A7HS5.mjs")),
  usersCheckmark: lazy(() => import("./UsersCheckmark-NUPRLRKD.mjs")),
  usersCheckmarkFilled: lazy(() => import("./UsersCheckmarkFilled-S5XRZ644.mjs")),
  usersFilled: lazy(() => import("./UsersFilled-CIZWWY73.mjs")),
  usersLock: lazy(() => import("./UsersLock-ZZNVH2ME.mjs")),
  usersLockFilled: lazy(() => import("./UsersLockFilled-AC34B45M.mjs")),
  usersPayroll: lazy(() => import("./UsersPayroll-DYLYF6FL.mjs")),
  usersPayrollFilled: lazy(() => import("./UsersPayrollFilled-3IABKUKW.mjs")),
  usersRemove: lazy(() => import("./UsersRemove-OUIRLHGD.mjs")),
  usersRemoveFilled: lazy(() => import("./UsersRemoveFilled-WUXLZJVQ.mjs")),
  usersTerminate: lazy(() => import("./UsersTerminate-T2GUCCAI.mjs")),
  usersTerminateFilled: lazy(() => import("./UsersTerminateFilled-S5BSC65Q.mjs"))
};

// src/Icon.tsx
import { jsx } from "react/jsx-runtime";
var StyledIconWrapper = styled.div(
  ({ size, color }) => ({
    color: color ?? "inherit",
    width: size,
    height: size,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    flexGrow: 0
  })
);
var IconStencil = memo(({ size }) => {
  const { borders } = useTheme();
  const style = useMemo(
    () => ({ borderRadius: borders.radius.circle }),
    [borders.radius.circle]
  );
  return /* @__PURE__ */ jsx(GenericStencil, { width: size, height: size, style });
});
var Icon = ({
  size,
  name,
  style: rawStyle,
  color,
  ...props
}) => {
  const DesiredIcon = lazilyLoadedIcons[name];
  const style = useMemo(
    () => ({ ...rawStyle, flex: 1 }),
    [rawStyle]
  );
  if (!DesiredIcon) {
    console.error("No icon for name `%s`", name);
    return /* @__PURE__ */ jsx(IconStencil, { size });
  }
  return /* @__PURE__ */ jsx(Suspense, { fallback: /* @__PURE__ */ jsx(IconStencil, { size }), children: /* @__PURE__ */ jsx(StyledIconWrapper, { size, color, children: /* @__PURE__ */ jsx(DesiredIcon, { ...props, style }) }) });
};
var iconNames = Object.freeze(
  Object.keys(lazilyLoadedIcons)
);
var isIconName = (value) => typeof value === "string" && lazilyLoadedIcons.hasOwnProperty(value);

// src/index.ts
var Icon2 = Icon;
Icon2.Stencil = IconStencil;
export {
  Icon2 as Icon,
  iconNames,
  iconSize,
  isIconName
};
